import React, { Dispatch, SetStateAction } from 'react';
import classnames from 'classnames';
import FluidButton from 'ui/FluidButton';
import * as UiActions from 'store/modules/ui/actions';
import * as AuthSelectors from 'store/modules/auth/selectors';
import * as BookingBuilderActions from 'store/modules/bookingBuilder/actions';
import * as BookingBuilderSelectors from 'store/modules/bookingBuilder/selectors';
import * as AgentsSelectors from 'store/modules/agents/selectors';
import { useCurrentWidth } from 'effects';
import { useDispatch, useSelector } from 'react-redux';
import { RIGHT_COLUMN_BREAKPOINT } from './const';
import { StandardModal } from 'pureUi/Modal';
import { BookingBuilder, ICompany, makeBackendApi } from 'services/BackendApi';
import { useQueryClient } from '@tanstack/react-query';
import { selectedTaSelector } from 'store/modules/agents';
import { useTranslation } from 'react-i18next';
import { isBookingOnRequestV2 } from 'store/modules/bookings';
import { useRequestBooking } from './hooks/useRequestBooking';
import { LodgingSummary, TCountryCode } from 'interfaces';
import { LiveRatePricesModal } from 'containers/HotelContainer/LiveRatePricesModal';
import { BasketSaveQuoteProposalButtons } from './BasketSaveQuoteProposalButtons';
import * as ActingOnBehalfOfSelectors from 'store/modules/actingOnBehalfOf/selectors';
import { IBasketBuildL4 } from 'services/BackendApi/types/Basket';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { MobileBottomDrawer } from 'ui/MobileBottomDrawer/MobileBottomDrawer';

interface IFourBookButtonsProps {
  bookingBuild: BookingBuilder | null;
  lodgingSummaries: LodgingSummary[];
  className?: string;
  canBook: boolean;
  isRequestInProgress: boolean;
  setRequestInProgress: Dispatch<SetStateAction<boolean>>;
  setRedirectLocation: React.Dispatch<React.SetStateAction<string | null>>;
  setShouldShowLeaveAlert: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  isPrimaryButtonDisabled: boolean;
  buttonsDisabled: boolean;
  isLiveRatePricesModalOpen: boolean;
  setIsLiveRatePricesModalOpen: Dispatch<SetStateAction<boolean>>;
  liveRatePricesModalData: any;
  setLiveRatePricesModalData: Dispatch<SetStateAction<any>>;
  taMarginAmountOverride?: string;
  basketBuild: IBasketBuildL4;
  onPostRemove: () => void;
  company: ICompany;
  clientCountryCode: TCountryCode | null;
  setShowValidation: Dispatch<SetStateAction<boolean>>;
}

export const FourBookButtons: React.FC<IFourBookButtonsProps> = ({
  bookingBuild,
  lodgingSummaries,
  isLoading,
  canBook,
  isRequestInProgress,
  setRequestInProgress,
  setRedirectLocation,
  setShouldShowLeaveAlert,
  isPrimaryButtonDisabled,
  isLiveRatePricesModalOpen,
  setIsLiveRatePricesModalOpen,
  liveRatePricesModalData,
  setLiveRatePricesModalData,
  taMarginAmountOverride,
  basketBuild,
  onPostRemove,
  company,
  clientCountryCode,
  setShowValidation,
}) => {
  const { currentWidth } = useCurrentWidth();
  const { isMobile } = useResponsive();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const selectedTa = useSelector(selectedTaSelector);
  const backendApi = makeBackendApi(selectedTa?.uuid);
  const bookingDomain = useSelector(BookingBuilderSelectors.bookingSelector);

  const selectedCompanyMembership = {
    uuid: basketBuild.companyMembershipUuid,
  };

  const bbv1ClearBookingErrors = () => dispatch(BookingBuilderActions.bbv1ClearBookingErrorsAction());
  const bbv1AddBookingError = (error: any) => dispatch(BookingBuilderActions.bbv1AddBookingErrorAction(error));
  const enqueueNotification = (notification: any) => dispatch(UiActions.enqueueNotification(notification));

  const actingOnBehalfOfUser = useSelector(ActingOnBehalfOfSelectors.actingOnBehalfOfUserSelector);
  const containsLiveRates =
    bookingBuild?.response.potentialBooking.Accommodation.some(requestedAccommodation => {
      return requestedAccommodation.isLiveRate;
    }) || false;

  const isTa: boolean = useSelector(AuthSelectors.isTA);
  const allowBookingLiveRates =
    (!isTa && containsLiveRates) || (isTa && containsLiveRates && company?.enableInstantBooking);

  const { handleRequestToBookButton, handleLiveRatePricesModalConfirm, closeLiveRatePricesModal } = useRequestBooking({
    bookingBuild,
    setRequestInProgress,
    backendApi,
    enqueueNotification,
    selectedCompanyMembership,
    bookingDomain,
    bbv1AddBookingError,
    bbv1ClearBookingErrors,
    containsLiveRates,
    clientCountryCode,
    setShouldShowLeaveAlert,
    setRedirectLocation,
    allowBookingLiveRates,
    setIsLiveRatePricesModalOpen,
    liveRatePricesModalData,
    setLiveRatePricesModalData,
    taMarginAmountOverride,
    actingOnBehalfOfUser,
    selectedTa,
    basketBuild,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['basket'] }),
  });

  const isOnRequest = useSelector(isBookingOnRequestV2());
  const guestInfo = useSelector(BookingBuilderSelectors.guestInfoSelector);
  const guestInfoFilledIn = guestInfo.guestFirstName?.trim() && guestInfo.guestLastName?.trim();

  const buttonsDisabled = !canBook || isLoading;

  const bookLabel = allowBookingLiveRates ? t('buttons.bookLive') : undefined;
  const bookLabelIfOnRequest = isOnRequest ? t('buttons.bookOnRequest') : t('buttons.bookNow');

  return (
    <div className="booking-buttons-container">
      <div className="booking-buttons flex flex-col gap-[8px] mt-[14px]">
        <div
          className={classnames('booking-buttons-primary flex gap-[8px]', {
            'flex-col': currentWidth < RIGHT_COLUMN_BREAKPOINT,
          })}
        >
          {/* this is the primary "REQUEST TO BOOK" button */}
          <FluidButton
            type="primary"
            className={classnames(
              'booking-button-request-to-book flex-1 w-full font-semibold font-hurmegeometric-sans min-h-[55px] rounded-none uppercase',
              { 'hover:bg-green-prime hover:border-none': !(buttonsDisabled || isPrimaryButtonDisabled) }
            )}
            onClick={() => {
              if (!guestInfoFilledIn) {
                setShowValidation(true);
              } else {
                handleRequestToBookButton();
              }
            }}
            disabled={buttonsDisabled || isPrimaryButtonDisabled}
          >
            {bookLabel || bookLabelIfOnRequest}
          </FluidButton>
        </div>

        {/* these are the 3 buttons that appear below the request to book button */}
        <BasketSaveQuoteProposalButtons
          bookingBuild={bookingBuild}
          lodgingSummaries={lodgingSummaries}
          isRequestInProgress={isRequestInProgress}
          setShouldShowLeaveAlert={setShouldShowLeaveAlert}
          setRequestInProgress={setRequestInProgress}
          setRedirectLocation={setRedirectLocation}
          canBook={canBook}
          isLoading={isLoading}
          taMarginAmountOverride={taMarginAmountOverride}
          basketBuild={basketBuild}
          onPostRemove={onPostRemove}
          clientCountryCode={clientCountryCode}
          setShowValidation={setShowValidation}
        />
      </div>

      {bookingBuild && isLiveRatePricesModalOpen && (
        isMobile ? (
          <MobileBottomDrawer
            isOpen={isLiveRatePricesModalOpen}
            onClose={closeLiveRatePricesModal}
            title="Changes in rates"
            contentClassName="[&>*]:min-w-0 [&>*]:max-w-full"
          >
            <LiveRatePricesModal
              confirmButtonLabel="Book now"
              onConfirm={handleLiveRatePricesModalConfirm}
              onClose={closeLiveRatePricesModal}
              isConfirmDisabled={isRequestInProgress}
              liveRates={liveRatePricesModalData}
              currency={bookingBuild.response.currency}
              total={bookingBuild.response?.totals?.totalForPricedItemsCents ?? 0}
            />
          </MobileBottomDrawer>
        ) : (
          <StandardModal removePadding={true} showCloseButton={false}>
            <LiveRatePricesModal
              confirmButtonLabel="Book now"
              onConfirm={handleLiveRatePricesModalConfirm}
              onClose={closeLiveRatePricesModal}
              isConfirmDisabled={isRequestInProgress}
              liveRates={liveRatePricesModalData}
              currency={bookingBuild.response.currency}
              total={bookingBuild.response?.totals?.totalForPricedItemsCents ?? 0}
            />
          </StandardModal>
        )
      )}
    </div>
  );
};
